/**
 * Engagement (human-interaction) agent settings — self-contained card with its
 * own debounced autosave. Pro-gated via workspace.has_human_interaction.
 */
import React, { useEffect, useState } from 'react';
import { Card, FormField, Input, useToast } from '../ui';
import type { Workspace } from '../../types';
import { ToggleRow, SaveStatusPill, useWorkspaceAutosave } from './agentsCommon';

interface Props {
  workspace: Workspace;
  onWorkspaceUpdate: (_ws: Workspace) => void;
}

export default function EngagementSettings({ workspace, onWorkspaceUpdate }: Props) {
  const { toast } = useToast();
  const canUse = workspace.has_human_interaction ?? false;
  const { saveStatus, queueSave } = useWorkspaceAutosave(
    workspace.id,
    onWorkspaceUpdate,
    (msg) => toast(msg, 'error')
  );

  const [enabled, setEnabled] = useState(workspace.interaction_enabled ?? false);
  const [replyComments, setReplyComments] = useState(
    workspace.interaction_auto_reply_comments ?? false
  );
  const [replyDms, setReplyDms] = useState(workspace.interaction_auto_reply_dms ?? false);
  const [replyRequests, setReplyRequests] = useState(
    workspace.interaction_auto_reply_requests ?? false
  );
  const [intervalHours, setIntervalHours] = useState(
    String(workspace.interaction_interval_hours ?? 1)
  );
  const [maxReplies, setMaxReplies] = useState(
    String(workspace.interaction_max_replies_per_run ?? 10)
  );

  useEffect(() => {
    setEnabled(workspace.interaction_enabled ?? false);
    setReplyComments(workspace.interaction_auto_reply_comments ?? false);
    setReplyDms(workspace.interaction_auto_reply_dms ?? false);
    setReplyRequests(workspace.interaction_auto_reply_requests ?? false);
    setIntervalHours(String(workspace.interaction_interval_hours ?? 1));
    setMaxReplies(String(workspace.interaction_max_replies_per_run ?? 10));
  }, [workspace]);

  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Let Conistra read and reply to your incoming comments and DMs on a schedule.">
          Settings
          {!canUse && <span className="settings-plan-lock-badge">🔒 Pro</span>}
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        {!canUse && (
          <div className="settings-entitlement-notice">
            The Engagement Agent is available on the <strong>Pro plan</strong>. Upgrade to let
            Conistra answer your audience automatically.
          </div>
        )}
        <ToggleRow
          label="Enable Engagement Agent"
          description={
            canUse
              ? 'Every hour the agent checks for new comments and DMs and drafts on-brand replies.'
              : '🔒 Upgrade to Pro to enable the Engagement Agent.'
          }
          checked={enabled && canUse}
          onChange={(v) => {
            if (!canUse) return;
            setEnabled(v);
            queueSave({ interaction_enabled: v });
          }}
          disabled={!canUse}
        />

        {enabled && canUse && (
          <>
            <ToggleRow
              label="Auto-send replies to comments"
              description="Send replies to comments on your posts automatically. Off = replies are drafted but not sent."
              checked={replyComments}
              onChange={(v) => {
                setReplyComments(v);
                queueSave({ interaction_auto_reply_comments: v });
              }}
            />
            <ToggleRow
              label="Auto-send replies to DMs"
              description="Reply to direct messages in your inbox automatically. Off = replies are drafted but not sent."
              checked={replyDms}
              onChange={(v) => {
                setReplyDms(v);
                queueSave({ interaction_auto_reply_dms: v });
              }}
            />
            <ToggleRow
              label="Auto-send replies to message requests"
              description="Reply to pending message requests automatically. Highest spam risk — leave off unless you trust your inbound."
              checked={replyRequests}
              onChange={(v) => {
                setReplyRequests(v);
                queueSave({ interaction_auto_reply_requests: v });
              }}
            />
            <div className="settings-number-row">
              <FormField label="Interval (hours)" htmlFor="ix-interval" hint="How many hours between runs. 1 = hourly.">
                <Input
                  id="ix-interval"
                  type="number"
                  min={1}
                  max={168}
                  value={intervalHours}
                  onChange={(e) => {
                    setIntervalHours(e.target.value);
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n) && n >= 1) queueSave({ interaction_interval_hours: n });
                  }}
                />
              </FormField>
              <FormField label="Max interactions / run" htmlFor="ix-max" hint="Cap on how many new interactions the agent handles each run (max 10).">
                <Input
                  id="ix-max"
                  type="number"
                  min={1}
                  max={10}
                  value={maxReplies}
                  onChange={(e) => {
                    setMaxReplies(e.target.value);
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n) && n >= 1 && n <= 10)
                      queueSave({ interaction_max_replies_per_run: Math.min(n, 10) });
                  }}
                />
              </FormField>
            </div>
          </>
        )}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <SaveStatusPill status={saveStatus} />
        </div>
      </Card.Body>
    </Card>
  );
}
